// Evidence Capture Service
// Handles photo and signature capture with Firebase Storage

import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { collection, addDoc, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { storage, db } from '../firebase';

export class EvidenceService {
  constructor() {
    this.evidenceCollection = collection(db, 'evidence');
    this.storageRef = ref(storage, 'evidence');
  }
  
  /**
   * Capture photo evidence
   */
  async capturePhoto(taskId, stepId, actionId, photoFile, evidenceType = 'during', options = {}) {
    try {
      if (!photoFile) throw new Error('Photo file is required');
      
      // Compress image if needed (client-side compression)
      const compressedFile = await this.compressImage(photoFile);
      
      // Generate unique filename
      const timestamp = Date.now();
      const filename = `${taskId}_${stepId}_${actionId}_${timestamp}.jpg`;
      const fileRef = ref(this.storageRef, filename);
      
      // Upload to Firebase Storage
      await uploadBytes(fileRef, compressedFile);
      
      // Get download URL
      const photoUrl = await getDownloadURL(fileRef);
      
      // Create evidence record in Firestore
      const evidenceData = {
        task_id: taskId,
        task_execution_id: options.executionId || null,
        step_id: stepId,
        action_id: actionId || null,
        
        // Evidence metadata
        evidence_type: 'photo',
        evidence_subtype: evidenceType, // 'before', 'during', 'after'
        photo_url: photoUrl,
        filename: filename,
        
        // Metadata
        timestamp: Timestamp.now(),
        location: options.location || null,
        executor_id: options.executorId || null,
        executor_name: options.executorName || null,
        notes: options.notes || null,
        
        // Validation
        validated: false,
        validation_status: 'pending',
        validated_by: null,
        validated_at: null
      };
      
      const docRef = await addDoc(this.evidenceCollection, evidenceData);
      
      console.log(`✅ Captured photo evidence: ${docRef.id}`);
      return {
        id: docRef.id,
        ...evidenceData
      };
    } catch (error) {
      console.error('Error capturing photo:', error);
      throw error;
    }
  }
  
  /**
   * Capture signature evidence
   */
  async captureSignature(taskId, executorId, signatureDataUrl, options = {}) {
    try {
      if (!signatureDataUrl) throw new Error('Signature data URL is required');
      
      // Convert data URL to blob
      const blob = await this.dataURLToBlob(signatureDataUrl);
      
      // Generate unique filename
      const timestamp = Date.now();
      const filename = `signature_${taskId}_${timestamp}.png`;
      const fileRef = ref(this.storageRef, filename);
      
      // Upload to Firebase Storage
      await uploadBytes(fileRef, blob);
      
      // Get download URL
      const signatureUrl = await getDownloadURL(fileRef);
      
      // Create evidence record in Firestore
      const evidenceData = {
        task_id: taskId,
        task_execution_id: options.executionId || null,
        
        // Evidence metadata
        evidence_type: 'signature',
        signature_url: signatureUrl,
        filename: filename,
        
        // Metadata
        timestamp: Timestamp.now(),
        executor_id: executorId,
        executor_name: options.executorName || null,
        notes: options.notes || null,
        
        // Validation
        validated: false,
        validation_status: 'pending',
        validated_by: null,
        validated_at: null
      };
      
      const docRef = await addDoc(this.evidenceCollection, evidenceData);
      
      console.log(`✅ Captured signature evidence: ${docRef.id}`);
      return {
        id: docRef.id,
        ...evidenceData
      };
    } catch (error) {
      console.error('Error capturing signature:', error);
      throw error;
    }
  }
  
  /**
   * Get evidence for task
   */
  async getEvidence(taskId, filters = {}) {
    try {
      let q = query(this.evidenceCollection, where('task_id', '==', taskId));
      
      if (filters.stepId) {
        q = query(q, where('step_id', '==', filters.stepId));
      }
      
      if (filters.actionId) {
        q = query(q, where('action_id', '==', filters.actionId));
      }
      
      if (filters.evidenceType) {
        q = query(q, where('evidence_type', '==', filters.evidenceType));
      }
      
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting evidence:', error);
      throw error;
    }
  }
  
  /**
   * Validate evidence
   */
  async validateEvidence(evidenceId, validationResult, userId) {
    try {
      const evidenceDoc = await getDocs(query(
        this.evidenceCollection,
        where('__name__', '==', evidenceId)
      ));
      
      if (evidenceDoc.empty) {
        throw new Error('Evidence not found');
      }
      
      // Update validation status (evidence records are typically immutable, 
      // so we'd create a validation record instead)
      // For now, just return validation result
      
      return {
        validated: validationResult.valid,
        validation_status: validationResult.valid ? 'approved' : 'rejected',
        validated_by: userId,
        validated_at: Timestamp.now(),
        validation_notes: validationResult.notes || null
      };
    } catch (error) {
      console.error('Error validating evidence:', error);
      throw error;
    }
  }
  
  /**
   * Compress image (client-side)
   */
  async compressImage(file, maxWidth = 1920, maxHeight = 1080, quality = 0.8) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const img = new Image();
        
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Calculate new dimensions
          if (width > maxWidth || height > maxHeight) {
            if (width > height) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            } else {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(new File([blob], file.name, { type: 'image/jpeg' }));
            } else {
              reject(new Error('Failed to compress image'));
            }
          }, 'image/jpeg', quality);
        };
        
        img.onerror = reject;
        img.src = e.target.result;
      };
      
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
  
  /**
   * Convert data URL to blob
   */
  async dataURLToBlob(dataURL) {
    const response = await fetch(dataURL);
    return await response.blob();
  }
}

export const evidenceService = new EvidenceService();



